import { useEffect } from "react";
import { BUSINESS, SERVICES, SERVED_AREAS, INSTAGRAM } from "@/data/content";
import { T } from "@/data/translations";

function setMeta(attr, key, content) {
  let el = document.head.querySelector(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

function setLink(rel, href, hreflang) {
  const selector = hreflang
    ? `link[rel="${rel}"][hreflang="${hreflang}"]`
    : `link[rel="${rel}"]:not([hreflang])`;
  let el = document.head.querySelector(selector);
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", rel);
    if (hreflang) el.setAttribute("hreflang", hreflang);
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
}

function buildJsonLd(lang) {
  const services = SERVICES.map((s) => ({
    "@type": "Service",
    name: s[lang].name,
    description: s[lang].desc,
    serviceType: s[lang].name,
    provider: { "@type": "BeautySalon", name: BUSINESS.brand },
    areaServed: { "@type": "City", name: "Oslo" },
  }));

  return {
    "@context": "https://schema.org",
    "@type": "BeautySalon",
    name: BUSINESS.brand,
    description: T[lang].metaDescription,
    url: BUSINESS.url,
    telephone: BUSINESS.phoneDisplay,
    email: BUSINESS.email,
    image: BUSINESS.url + "/og-image.jpg",
    sameAs: [INSTAGRAM.url],
    priceRange: "$$",
    currenciesAccepted: "NOK",
    address: {
      "@type": "PostalAddress",
      streetAddress: BUSINESS.street,
      postalCode: BUSINESS.postalCode,
      addressLocality: BUSINESS.city,
      addressRegion: BUSINESS.region,
      addressCountry: BUSINESS.countryCode,
    },
    geo: {
      "@type": "GeoCoordinates",
      latitude: BUSINESS.geo.lat,
      longitude: BUSINESS.geo.lng,
    },
    areaServed: SERVED_AREAS.map((a) => ({ "@type": "Place", name: a })),
    openingHoursSpecification: [
      {
        "@type": "OpeningHoursSpecification",
        dayOfWeek: [
          "Monday",
          "Tuesday",
          "Wednesday",
          "Thursday",
          "Friday",
          "Saturday",
          "Sunday",
        ],
        opens: BUSINESS.hours.open,
        closes: BUSINESS.hours.close,
      },
    ],
    makesOffer: services.map((svc) => ({
      "@type": "Offer",
      itemOffered: svc,
    })),
    hasOfferCatalog: {
      "@type": "OfferCatalog",
      name: lang === "en" ? "Beauty services" : "Skj\u00f8nnhetstjenester",
      itemListElement: services,
    },
  };
}

export function useSeo(lang) {
  useEffect(() => {
    const t = T[lang];

    document.documentElement.lang = lang;
    document.title = t.metaTitle;

    setMeta("name", "description", t.metaDescription);
    setMeta("name", "robots", "index, follow");

    // Open Graph
    setMeta("property", "og:type", "website");
    setMeta("property", "og:site_name", BUSINESS.brand);
    setMeta("property", "og:title", t.metaTitle);
    setMeta("property", "og:description", t.metaDescription);
    setMeta("property", "og:locale", lang === "no" ? "nb_NO" : "en_US");
    setMeta("property", "og:url", BUSINESS.url);
    setMeta("property", "og:image", BUSINESS.url + "/og-image.jpg");
    setMeta("property", "og:image:width", "1200");
    setMeta("property", "og:image:height", "630");
    setMeta("property", "og:image:alt", t.metaTitle);

    // Twitter
    setMeta("name", "twitter:card", "summary_large_image");
    setMeta("name", "twitter:title", t.metaTitle);
    setMeta("name", "twitter:description", t.metaDescription);
    setMeta("name", "twitter:image", BUSINESS.url + "/og-image.jpg");

    // Canonical + hreflang
    setLink("canonical", BUSINESS.url + "/");
    setLink("alternate", BUSINESS.url + "/?lang=no", "nb-NO");
    setLink("alternate", BUSINESS.url + "/?lang=en", "en");
    setLink("alternate", BUSINESS.url + "/", "x-default");

    // JSON-LD structured data
    let script = document.getElementById("bbm-jsonld");
    if (!script) {
      script = document.createElement("script");
      script.type = "application/ld+json";
      script.id = "bbm-jsonld";
      document.head.appendChild(script);
    }
    script.textContent = JSON.stringify(buildJsonLd(lang));
  }, [lang]);
}
